import type { ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';

export function useGetMyContractTypes(): {
  loading: boolean;
  myContractTypes: NonNullable<Apollo.GetMyContractTypesQueryResult['data']>['myContractTypes'];
  refetch: Apollo.GetMyContractTypesQueryResult['refetch'];
} {
  const { data, loading, refetch } = Apollo.useGetMyContractTypesQuery();

  return {
    loading,
    myContractTypes: data?.myContractTypes ?? [],
    refetch,
  };
}

export function useGetMyContractTypesWithDocCount(
  options?: Parameters<typeof Apollo.useGetMyContractTypesWithDocumentCountQuery>[0]
): {
  loading: boolean;
  myContractTypes: NonNullable<
    Apollo.GetMyContractTypesWithDocumentCountQueryResult['data']
  >['myContractTypes'];
  refetch: Apollo.GetMyContractTypesQueryResult['refetch'];
} {
  const { data, loading, refetch } = Apollo.useGetMyContractTypesWithDocumentCountQuery(options);

  return {
    loading,
    myContractTypes: data?.myContractTypes ?? [],
    refetch,
  };
}

/**
 * Create a ContractType.
 */
export function useCreateContractTypes(): {
  createContractTypes: (
    options: Parameters<Apollo.CreateContractTypesMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateContractTypesMutation>>;
  loading: boolean;
} {
  const [createContractTypes, { loading }] = Apollo.useCreateContractTypesMutation();

  return {
    createContractTypes: options => {
      return createContractTypes({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              myContractTypes: (refs = []) => {
                if (!data?.createContractTypes) return refs;

                const newContractTypeRefs = data.createContractTypes.map(data => {
                  return cache.writeFragment({
                    data,
                    fragment: gql`
                      fragment NewContractType on ContractType {
                        __typename
                        id
                      }
                    `,
                  });
                });
                return [...newContractTypeRefs, ...refs];
              },
            },
          });
        },
      });
    },
    loading,
  };
}

export function useUpdateContractType(): {
  loading: boolean;
  updateContractType: (
    options: NonNullable<Parameters<Apollo.UpdateContractTypeMutationHookResult[0]>[0]>
  ) => Promise<ApolloLink.Result<Apollo.UpdateContractTypeMutation>>;
} {
  const [updateContractType, { loading }] = Apollo.useUpdateContractTypeMutation();

  return {
    loading,
    updateContractType: options => {
      return updateContractType({
        ...options,
        optimisticResponse: vars => ({
          __typename: 'Mutation',
          updateContractType: {
            __typename: 'ContractType',
            id: vars.input.id,
            value: vars.input.props.value,
          },
        }),
      });
    },
  };
}

export function useDeleteContractType(): {
  deleteContractType: (
    options: NonNullable<Parameters<Apollo.DeleteContractTypeMutationHookResult[0]>[0]>
  ) => Promise<ApolloLink.Result<Apollo.DeleteContractTypeMutation>>;
  loading: boolean;
} {
  const [deleteContractType, { loading }] = Apollo.useDeleteContractTypeMutation();

  return {
    deleteContractType: options => {
      return deleteContractType({
        ...options,
        update: cache => {
          const myContractTypes: keyof Apollo.Query = 'myContractTypes';

          cache.modify({
            fields: {
              [myContractTypes]: (refs: readonly Reference[] = [], { readField }) => {
                return refs.filter(
                  (ref: Reference) =>
                    readField('id', ref) !== options.variables?.input.deletedTypeId
                );
              },
            },
          });
        },
      });
    },
    loading,
  };
}

export function useMergeContractTypes(): {
  loading: boolean;
  mergeContractTypes: (
    options: Parameters<Apollo.MergeContractTypesMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.MergeContractTypesMutation>>;
} {
  const [mergeContractTypes, { loading }] = Apollo.useMergeContractTypesMutation();

  return {
    loading,
    mergeContractTypes: options => {
      return mergeContractTypes({
        ...options,
        update: (cache, { data }) => {
          // Mutation failed.  Don't update cache.
          if (!data) return;

          const typesKey: keyof Apollo.Query = 'myContractTypes';
          const sourceTypeIds = new Set(options?.variables?.input.sourceTypeIds ?? []);

          cache.modify({
            fields: {
              [typesKey]: (refs: readonly Reference[] = [], { readField }) => {
                return refs.filter((ref: Reference) => {
                  const typeId = readField<UUID>('id', ref);
                  return typeId && !sourceTypeIds.has(typeId);
                });
              },
            },
          });
        },
      });
    },
  };
}

/**
 * Update a Document's ContractType.
 */
export function useUpdateDocumentType(): {
  loading: boolean;
  updateDocumentType: (
    options: NonNullable<Parameters<Apollo.UpdateDocumentTypeMutationHookResult[0]>[0]> & {
      optimisticType: Pick<Apollo.ContractType, 'id' | '__typename' | 'value'> | null;
    }
  ) => Promise<ApolloLink.Result<Apollo.UpdateDocumentTypeMutation>>;
} {
  const [updateDocumentType, { loading }] = Apollo.useUpdateDocumentTypeMutation();

  return {
    loading,
    updateDocumentType: ({ optimisticType, ...apolloOptions }) => {
      return updateDocumentType({
        ...apolloOptions,
        optimisticResponse: vars => ({
          __typename: 'Mutation',
          updateDocumentType: {
            __typename: 'Document',
            id: vars.input.documentId,
            type: optimisticType,
          },
        }),
      });
    },
  };
}

/**
 * Checks whether a user is authorized to edit contract types.
 */
export function useCanModifyTypes(): {
  canModifyTypes: boolean;
} {
  const currentUser = useSelectCurrentUser();
  return { canModifyTypes: currentUser?.role === Apollo.OrgRole.Admin };
}
